import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShoppingBag, X, SlidersHorizontal, Filter, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SORT_OPTIONS = [
    { value: "featured", label: "Featured" },
    { value: "best_sellers", label: "Best Sellers" },
    { value: "price_asc", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
    { value: "newest", label: "Newest" },
];

function ProductCard({ p, onAdd }) {
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const savings = onSale ? Math.round(((p.compare_at_price - p.price) / p.compare_at_price) * 100) : 0;
    const outOfStock = (p.stock ?? 0) <= 0;
    return (
        <article className="group bg-white rounded-2xl overflow-hidden shadow-soft border border-gray-100 transition-all duration-300 hover:shadow-brand hover:-translate-y-0.5" data-testid={`product-card-${p.id}`}>
            <Link to={`/products/${p.id}`} className="block relative aspect-square overflow-hidden bg-gray-50" data-testid={`product-image-link-${p.id}`}>
                {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                    <span className="absolute inset-0 flex items-center justify-center text-ink-400 text-xs">No image</span>
                )}
                {onSale && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-white text-[11px] font-semibold uppercase tracking-wider shadow-accent">
                        -{savings}%
                    </span>
                )}
                {outOfStock && (
                    <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-ink-900 text-white text-[11px] font-semibold uppercase tracking-wider">
                        Out of stock
                    </span>
                )}
            </Link>
            <div className="p-4">
                <div className="text-[11px] uppercase tracking-wider text-ink-400">{p.category}</div>
                <Link to={`/products/${p.id}`} className="block">
                    <h3 className="font-heading text-base font-semibold text-ink-900 mt-0.5 hover:text-brand-600 transition-colors line-clamp-1">{p.name}</h3>
                </Link>
                <p className="text-xs text-ink-700 mt-1 line-clamp-2 leading-relaxed">{p.description}</p>
                <div className="mt-3 flex items-end justify-between gap-2">
                    <div className="leading-tight">
                        <div className="font-heading text-lg font-semibold text-ink-900">₹{p.price.toLocaleString("en-IN")}</div>
                        {onSale && (
                            <div className="text-[11px] text-ink-400 line-through">₹{p.compare_at_price.toLocaleString("en-IN")}</div>
                        )}
                    </div>
                    <Button
                        size="sm"
                        disabled={outOfStock}
                        onClick={() => onAdd(p)}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta h-9 px-3 text-xs disabled:opacity-50"
                        data-testid={`product-add-${p.id}`}
                    >
                        <ShoppingBag className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                </div>
            </div>
        </article>
    );
}

function FilterSection({ title, children }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold mb-3">{title}</div>
            {children}
        </div>
    );
}

function PriceRange({ min, max, value, onChange }) {
    // Two numeric inputs. Keep it simple & accessible.
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <input
                    type="number"
                    placeholder={`Min ₹${min}`}
                    value={value[0] ?? ""}
                    onChange={(e) => onChange([e.target.value === "" ? null : Number(e.target.value), value[1]])}
                    min={0}
                    className="w-1/2 px-3 py-1.5 rounded-xl border border-gray-200 text-sm focus:border-brand-300 outline-none"
                    data-testid="filter-price-min"
                />
                <span className="text-ink-400 text-xs">to</span>
                <input
                    type="number"
                    placeholder={`Max ₹${max}`}
                    value={value[1] ?? ""}
                    onChange={(e) => onChange([value[0], e.target.value === "" ? null : Number(e.target.value)])}
                    min={0}
                    className="w-1/2 px-3 py-1.5 rounded-xl border border-gray-200 text-sm focus:border-brand-300 outline-none"
                    data-testid="filter-price-max"
                />
            </div>
            <div className="text-[10px] text-ink-400">Range available: ₹{min.toLocaleString("en-IN")} – ₹{max.toLocaleString("en-IN")}</div>
        </div>
    );
}

function Toggle({ label, checked, onChange, testId, hint }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                data-testid={testId}
            />
            <span className="text-ink-900">{label}</span>
            {hint && <span className="text-[10px] text-ink-400">({hint})</span>}
        </label>
    );
}

export default function ProductsListPage({ onOpenAuth }) {
    const [params, setParams] = useSearchParams();
    const { addItem } = useCart();
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [facets, setFacets] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const categoryId = params.get("category_id") || "";
    const priceMin = params.get("price_min") ? Number(params.get("price_min")) : null;
    const priceMax = params.get("price_max") ? Number(params.get("price_max")) : null;
    const onSale = params.get("on_sale") === "1";
    const inStock = params.get("in_stock") === "1";
    const bestSellers = params.get("best_sellers") === "1";
    const sort = params.get("sort") || "featured";
    const selectedAttrs = useMemo(() => {
        const out = {};
        for (const tok of params.getAll("attr")) {
            const [a, v] = tok.split(":");
            if (!a || !v) continue;
            (out[a] ||= []).push(v);
        }
        return out;
    }, [params]);

    const updateParams = (mut) => {
        const sp = new URLSearchParams(params);
        mut(sp);
        setParams(sp, { replace: true });
    };

    useEffect(() => {
        api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
    }, []);

    useEffect(() => {
        const sp = new URLSearchParams();
        if (categoryId) sp.set("category_id", categoryId);
        api.get(`/products/facets?${sp.toString()}`).then(({ data }) => setFacets(data)).catch(() => setFacets(null));
    }, [categoryId]);

    useEffect(() => {
        setLoading(true);
        const sp = new URLSearchParams();
        if (categoryId) sp.set("category_id", categoryId);
        if (priceMin !== null) sp.set("price_min", String(priceMin));
        if (priceMax !== null) sp.set("price_max", String(priceMax));
        if (onSale) sp.set("on_sale", "true");
        if (inStock) sp.set("in_stock", "true");
        if (bestSellers) sp.set("best_sellers_only", "true");
        sp.set("sort", sort);
        for (const [a, vs] of Object.entries(selectedAttrs)) {
            for (const v of vs) sp.append("attr", `${a}:${v}`);
        }
        api.get(`/products?${sp.toString()}`)
            .then(({ data }) => setProducts(data || []))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, [categoryId, priceMin, priceMax, onSale, inStock, bestSellers, sort, selectedAttrs]);

    const onAdd = (p) => {
        addItem({ id: p.id, kind: "product", name: p.name, price: p.price, image_url: p.image_url, meta: p.category }, 1);
        toast.success("Added to cart", { description: p.name });
    };

    const toggleAttr = (a, v) => {
        const tok = `${a}:${v}`;
        const all = params.getAll("attr");
        const next = all.includes(tok) ? all.filter((x) => x !== tok) : [...all, tok];
        updateParams((sp) => {
            sp.delete("attr");
            for (const t of next) sp.append("attr", t);
        });
    };

    const clearAll = () => setParams(new URLSearchParams(), { replace: true });

    const fMin = facets?.price_min ?? 0;
    const fMax = facets?.price_max ?? 0;
    const activeChips = [];
    if (categoryId) {
        const cat = categories.find((c) => c.id === categoryId);
        activeChips.push({ label: `Category: ${cat?.name || categoryId}`, clear: () => updateParams((sp) => sp.delete("category_id")) });
    }
    if (priceMin !== null) activeChips.push({ label: `Min ₹${priceMin}`, clear: () => updateParams((sp) => sp.delete("price_min")) });
    if (priceMax !== null) activeChips.push({ label: `Max ₹${priceMax}`, clear: () => updateParams((sp) => sp.delete("price_max")) });
    if (onSale) activeChips.push({ label: "On sale", clear: () => updateParams((sp) => sp.delete("on_sale")) });
    if (inStock) activeChips.push({ label: "In stock", clear: () => updateParams((sp) => sp.delete("in_stock")) });
    if (bestSellers) activeChips.push({ label: "Best sellers", clear: () => updateParams((sp) => sp.delete("best_sellers")) });
    for (const [a, vs] of Object.entries(selectedAttrs)) {
        const attr = facets?.attributes?.find((at) => at.id === a);
        for (const v of vs) {
            const val = attr?.values?.find((x) => x.id === v);
            if (val && attr) activeChips.push({ label: `${attr.name}: ${val.value}`, clear: () => toggleAttr(a, v) });
        }
    }

    return (
        <div className="min-h-screen bg-paper">
            <Navbar onOpenAuth={onOpenAuth} />
            <main>
                <section className="relative bg-paper-alt border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                        <div className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold flex items-center gap-1.5">
                            <Tag className="h-3.5 w-3.5" /> Shop
                        </div>
                        <h1 className="font-heading text-4xl md:text-5xl font-semibold text-ink-900 mt-2">Our products</h1>
                        <p className="mt-3 text-base md:text-lg text-ink-700 max-w-2xl">
                            Mats, props, apparel and tools to support your daily practice — sustainably made and lovingly curated.
                        </p>
                    </div>
                </section>

                <section className="py-10 md:py-14">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-6">
                        {/* Sidebar */}
                        <aside className="lg:col-span-3 space-y-4" data-testid="products-filters-sidebar">
                            <div className="flex items-center justify-between">
                                <div className="font-heading text-lg font-semibold inline-flex items-center gap-1.5"><SlidersHorizontal className="h-4 w-4" /> Filters</div>
                                {activeChips.length > 0 && (
                                    <button onClick={clearAll} className="text-xs text-brand-700 hover:underline" data-testid="products-clear-all">Clear all</button>
                                )}
                            </div>

                            <FilterSection title="Category">
                                <div className="space-y-1.5">
                                    <button
                                        onClick={() => updateParams((sp) => sp.delete("category_id"))}
                                        className={`w-full text-left px-3 py-2 rounded-xl text-sm transition ${!categoryId ? "bg-brand-50 text-brand-700 font-medium" : "hover:bg-paper-alt"}`}
                                        data-testid="filter-category-all"
                                    >
                                        All
                                    </button>
                                    {(() => {
                                        // Render categories as a tree — parents first, sub-categories indented underneath.
                                        const byParent = new Map();
                                        for (const c of categories) {
                                            const k = c.parent_id || "__root__";
                                            if (!byParent.has(k)) byParent.set(k, []);
                                            byParent.get(k).push(c);
                                        }
                                        const ordered = [];
                                        const walk = (key, depth) => {
                                            for (const c of (byParent.get(key) || [])) {
                                                ordered.push({ c, depth });
                                                walk(c.id, depth + 1);
                                            }
                                        };
                                        walk("__root__", 0);
                                        const seen = new Set(ordered.map((x) => x.c.id));
                                        for (const c of categories) if (!seen.has(c.id)) ordered.push({ c, depth: 0 });
                                        return ordered.map(({ c, depth }) => (
                                            <button
                                                key={c.id}
                                                onClick={() => updateParams((sp) => sp.set("category_id", c.id))}
                                                style={depth ? { paddingLeft: 12 + depth * 14 } : undefined}
                                                className={`w-full text-left px-3 py-2 rounded-xl text-sm transition ${categoryId === c.id ? "bg-brand-50 text-brand-700 font-medium" : "hover:bg-paper-alt"} ${depth ? "text-ink-700" : ""}`}
                                                data-testid={`filter-category-${c.id}`}
                                            >
                                                {depth > 0 && <span className="text-ink-400 mr-1">↳</span>}{c.name}
                                            </button>
                                        ));
                                    })()}
                                </div>
                            </FilterSection>

                            <FilterSection title="Price">
                                <PriceRange
                                    min={fMin}
                                    max={fMax}
                                    value={[priceMin, priceMax]}
                                    onChange={([lo, hi]) => updateParams((sp) => {
                                        if (lo === null || lo === undefined || lo === "") sp.delete("price_min"); else sp.set("price_min", String(lo));
                                        if (hi === null || hi === undefined || hi === "") sp.delete("price_max"); else sp.set("price_max", String(hi));
                                    })}
                                />
                            </FilterSection>

                            <FilterSection title="Availability & deals">
                                <div className="space-y-2">
                                    <Toggle label="In stock only" checked={inStock} onChange={(v) => updateParams((sp) => v ? sp.set("in_stock", "1") : sp.delete("in_stock"))} testId="filter-in-stock" hint={facets ? `${facets.in_stock_count}` : null} />
                                    <Toggle label="On sale" checked={onSale} onChange={(v) => updateParams((sp) => v ? sp.set("on_sale", "1") : sp.delete("on_sale"))} testId="filter-on-sale" hint={facets ? `${facets.on_sale_count}` : null} />
                                    <Toggle label="Best sellers" checked={bestSellers} onChange={(v) => updateParams((sp) => v ? sp.set("best_sellers", "1") : sp.delete("best_sellers"))} testId="filter-best-sellers" />
                                </div>
                            </FilterSection>

                            {facets?.attributes?.map((a) => (
                                <FilterSection key={a.id} title={a.name}>
                                    <div className="flex flex-wrap gap-1.5">
                                        {a.values.map((v) => {
                                            const selected = (selectedAttrs[a.id] || []).includes(v.id);
                                            return (
                                                <button
                                                    key={v.id}
                                                    onClick={() => toggleAttr(a.id, v.id)}
                                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${selected ? "bg-brand-500 border-brand-500 text-white" : "bg-white border-gray-200 hover:border-brand-300"}`}
                                                    data-testid={`filter-attr-${a.id}-${v.id}`}
                                                >
                                                    {v.value}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </FilterSection>
                            ))}
                        </aside>

                        {/* Main grid */}
                        <div className="lg:col-span-9">
                            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                                <div className="text-sm text-ink-700" data-testid="products-result-count">
                                    {loading ? "Loading…" : `${products.length} product${products.length === 1 ? "" : "s"}`}
                                </div>
                                <Select value={sort} onValueChange={(v) => updateParams((sp) => sp.set("sort", v))}>
                                    <SelectTrigger className="w-56 rounded-full" data-testid="products-sort">
                                        <SelectValue placeholder="Sort by" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SORT_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {activeChips.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-5" data-testid="products-active-chips">
                                    {activeChips.map((c, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-medium">
                                            {c.label}
                                            <button onClick={c.clear} className="hover:text-brand-900" aria-label="Remove filter"><X className="h-3 w-3" /></button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {loading ? (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-72 rounded-2xl bg-gray-100 animate-pulse" />)}
                                </div>
                            ) : products.length === 0 ? (
                                <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
                                    <Filter className="h-6 w-6 text-ink-400 mx-auto mb-2" />
                                    <p className="text-ink-700">No products match your filters.</p>
                                    {activeChips.length > 0 && (
                                        <Button variant="outline" onClick={clearAll} className="mt-3 rounded-full">Clear filters</Button>
                                    )}
                                </div>
                            ) : (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
                                    {products.map((p) => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
